import { useState, useEffect, useRef } from "react";
import opleidingen from "../data/opleidingen.json";
import { supabase } from "../lib/supabase";

export default function ResultScreen({ answers, onRestart }) {
  const [activeIndex, setActiveIndex] = useState(0);

const safeAnswers = answers ?? {};
const q1Answer = safeAnswers.q1;
const q4Answer = safeAnswers.q4;
const q4Answers = Array.isArray(q4Answer) ? q4Answer : [q4Answer];

/* ✅ 1. Scores berekenen over ALLE opleidingen */
const scoredResults = opleidingen.map((o) => {
  let score = 0;

  Object.entries(safeAnswers).forEach(([qId, answer]) => {
    if (Array.isArray(answer)) {
      answer.forEach((key) => {
        score += o.weights?.[qId]?.[key] ?? 0;
      });
    } else {
      score += o.weights?.[qId]?.[answer] ?? 0;
    }
  });

  return { ...o, score };
});

/* ✅ 2. TOP 3: ALTIJD q4-prioriteit */
const q4Matches = q4Answers.length
  ? scoredResults
      .filter((o) =>
        q4Answers.some((key) => o.weights?.q4?.[key] > 0)
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
  : [];

/* ✅ 3. Overige kandidaten: moeten q1 matchen */
const remaining = scoredResults
  .filter(
    (o) =>
      !q4Matches.some((m) => m.name === o.name) &&
      (!q1Answer || o.weights?.q1?.[q1Answer] > 0)
  )
  .sort((a, b) => b.score - a.score);

/* ✅ 4. Samenstellen */
const top5 = [...q4Matches, ...remaining].slice(0, 5);

  const hasSavedRef = useRef(false);
    
  const top5Names = top5.map((item) => item.name);
  const maxScore = Math.max(...top5.map((item) => item.score), 1);

  useEffect(() => {
    if (!answers || Object.keys(answers).length === 0) return;

    // ✅ voorkomt multiple inserts (StrictMode + rerenders)
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;

    const saveResults = async () => {
      const { error } = await supabase.from("quiz_results").insert({
        top_results: top5Names,
        raw_answers: answers,
        session_id: crypto.randomUUID(),
        user_agent: navigator.userAgent,
      });

      if (error) {
        console.error("Supabase insert error:", error);
      }
    };

    saveResults();
  }, [answers]);

  // ✅ Render‑guard NA hooks
  if (!answers || Object.keys(answers).length === 0) {
    return null;
  }

  return (
    <div className="screen">
      <div className="card card--results">
        <h2>Jouw beste matches</h2>
        <p>
          Op basis van je antwoorden passen deze opleidingen het best bij jou.
        </p>

        <div className="results-grid">
          {top5.map((item, index) => (
          <a
            key={item.url}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`result-card ${
              index === activeIndex ? "is-active" : "is-inactive"
            }`}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(0)}
          >

              <div className="result-score">
                <div className="score-bar">
                  <div
                    className="score-fill"
                    style={{
                      width: `${Math.round(
                        (item.score / maxScore) * 100
                      )}%`,
                    }}
                  />
                </div>
                <span className="score-label">
                  {Math.round((item.score / maxScore) * 100)}% match
                </span>
              </div>

              <h3>{item.name}</h3>
              <p className="result-description">{item.description}</p>

              <ul className="result-meta">
                <li data-icon="🎓">{item.type}</li>
                <li data-icon="⏱">{item.duration}</li>
                <li data-icon="🏷️">{item.price}</li>
              </ul>

              <span className="result-link">
                <div className="progress-divider" />
                Bekijk ↗
              </span>
            </a>
          ))}
        </div>

        {/* ✅ OPNIEUW TESTEN */}
        <button
          className="next-button"
          style={{ marginTop: "32px" }}
          onClick={onRestart}
        >
          🔄 Test opnieuw doen
        </button>
      </div>
    </div>
  );
}
